/**
 * TAX STATEMENTS (Investor depth). A summary of taxable income, capital gains, and tax withheld, with a
 * downloadable statement per year. Figures are illustrative in the sandbox.
 */

import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { StatTile as Stat } from '@/components/dash';
import { ErrorState, LoadingCard } from '@/components/states';
import { useResource } from '@/lib/use-resource';

export default function Tax() {
  const { data, loading, error, reload } = useResource(() => api.tax(), []);

  if (loading)
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <LoadingCard lines={2} />
        <LoadingCard lines={4} />
      </div>
    );
  if (error) return <ErrorState body="We could not load your tax statements." onRetry={reload} />;
  if (!data) return null;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Link
        to="/earnings"
        className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 6l-6 6 6 6" />
        </svg>
        Earnings
      </Link>

      <header>
        <h1 className="text-page-title text-ink">Tax statements</h1>
        <p className="mt-0.5 text-sm text-ink-muted">Your income and gains, for your tax return.</p>
      </header>

      <section className="grid grid-cols-3 gap-3">
        <Stat label="Taxable income" value={data.summary.taxableIncome} />
        <Stat label="Capital gains" value={data.summary.capitalGains} />
        <Stat label="Tax withheld" value={data.summary.withheld} accent />
      </section>

      <section>
        <h2 className="mb-2 font-semibold text-ink">Annual statements</h2>
        <ul className="divide-y divide-border overflow-hidden rounded-card border border-border bg-surface shadow-card">
          {data.years.map((y) => (
            <li key={y.year} className="flex items-center gap-3 px-4 py-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-brand">
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.8}
                    d="M8 4h8a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Zm2 5h4m-4 3h4m-4 3h2"
                  />
                </svg>
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-ink">{y.year} statement</p>
                <p className="text-xs text-ink-subtle">
                  Income KES {y.income} &middot; Gains KES {y.gains}
                </p>
              </div>
              <button
                disabled={!y.ready}
                className="rounded-field border border-border px-3 py-1.5 text-sm font-medium text-ink transition hover:bg-surface-sunken disabled:opacity-40"
              >
                {y.ready ? 'Download' : 'Not ready'}
              </button>
            </li>
          ))}
        </ul>
      </section>

      <p className="rounded-card bg-surface-sunken p-3 text-center text-xs text-ink-muted">
        These figures are a summary to help you file. They are not tax advice, and amounts are
        illustrative in the sandbox.
      </p>
    </div>
  );
}
